import { useCallback, useEffect, useRef, useState } from 'react';
import useDataQuery from '../../../../../../hooks/useDataQuery/useDataQuery';
import { ExtractPaginationType } from '../../../../../../services/types';
import { Pagination } from '../../../../../../types/pagination.type';
import useWidth from '../../../../../Hooks/useWidth';
import {
  Option,
  OptionValue,
} from '../../../components/MultiOptionsSelectorFilter/components/MultiOptionsSelector/components/OptionsList/optionsList.types';
import { HandleResetToDefaultValue } from '../../commonHookReturn.types';
import useOptionsSelector from '../useOptionsSelector';
import {
  FindParams,
  GenericQueryOutput,
  GenericQueryParams,
  RecognizedFindParams,
  UpdateRecognizedFindParams,
  UseMultiDynamicOptionsSelectorOutput,
  UseMultiDynamicOptionsSelectorProps,
} from './useMultiDynamicOptionsSelector.types';
import { getOrderArg } from './useMultiDynamicOptionsSelector.utils';

const useMultiDynamicOptionsSelector = <
  QueryParams extends GenericQueryParams,
  QueryOutput extends GenericQueryOutput,
>({
  key,
  queryFn,
  queryKeyFn,
  formatLabel,
  formatChipLabel,
  order,
  paramKeyToSearchMore,
  paramKeyToSearchSpecifics,
  defaultValues,
  shouldIgnore,
  valueKey = 'id',
  defaultQueryParam = {},
  aggregatingOptionParams,
  shouldIgnoreDefaultFilters = true,
  debounceTime,
  dontHandleSearchParams,
  isDisabled,
}: UseMultiDynamicOptionsSelectorProps<
  QueryParams,
  QueryOutput
>): UseMultiDynamicOptionsSelectorOutput<QueryOutput> => {
  const { smallWidth } = useWidth();

  const DEFAULT_LIMIT = smallWidth ? 30 : 15;
  const FETCH_ON_MOUNT = !isDisabled && !!defaultValues?.length;

  const DEFAULT_FIND_PARAMS = {
    skip: 0,
    ...defaultQueryParam,
    limit: DEFAULT_LIMIT,
  } as QueryParams;

  const hasLoadedDefaultValuesRef = useRef(false);
  const hasNormalizedInitialValues = useRef(false);
  const recognizedFindParams = useRef<RecognizedFindParams[]>([]);

  const [canFetchData, setCanFetchData] = useState(FETCH_ON_MOUNT);
  const [findParams, setFindParams] = useState<QueryParams>(DEFAULT_FIND_PARAMS);
  const {
    data: rawDynamicData,
    isLoading,
    isFetched,
    isFetchedAfterMount,
  } = useDataQuery<Pagination<ExtractPaginationType<QueryOutput>> | undefined>(
    {
      queryKey: queryKeyFn({ ...getOrderArg(order), ...findParams }),
      queryFn: async () => {
        const response = await queryFn({ ...getOrderArg(order), ...findParams });
        if (!response.ok || !response.data) {
          return undefined;
        }
        return response.data as Pagination<ExtractPaginationType<QueryOutput>>;
      },
    },
    {
      enabled: canFetchData && !isDisabled,
    },
  );

  const [allOptions, setAllOptions] = useState<Option<ExtractPaginationType<QueryOutput>>[]>([]);
  const {
    handleUpdateInternalState,
    hasSelectedOptions,
    selectedOptions,
    setSelectedOptions,
    cleanFilter,
    getSearchParamKeys,
    formatDefaultValues,
    initialSelectedValuesFromSearchParams,
    normalizeSelectedValues,
  } = useOptionsSelector({
    key,
    defaultValues,
    shouldIgnoreDefaultFilters,
    shouldIgnore,
    debounceTime,
    dontHandleSearchParams,
    isDisabled,
  });

  const allOptionsRef = useRef(allOptions);
  useEffect(() => {
    allOptionsRef.current = allOptions;
  }, [allOptions]);

  const mountFindParams = ({ limit, skip, queryToSearchMore, optionsToFind }: FindParams) => {
    const hasOptionsToFind = optionsToFind && optionsToFind.length > 0;
    return {
      skip,
      [paramKeyToSearchMore]: queryToSearchMore,
      [paramKeyToSearchSpecifics]: optionsToFind?.map(String),
      ...defaultQueryParam,
      limit: hasOptionsToFind ? optionsToFind.length : limit,
    } as QueryParams;
  };

  const handleSetFindParams = (newFindParams: FindParams) => {
    setFindParams(mountFindParams(newFindParams));
  };

  const updateRecognizedFindParams = ({
    hasMoreData,
    newFindParams,
    currentFindParams,
  }: UpdateRecognizedFindParams) => {
    if (currentFindParams) {
      recognizedFindParams.current = recognizedFindParams.current.map((params) =>
        params.queryToSearchMore === currentFindParams.queryToSearchMore
          ? { ...newFindParams, hasMoreData, lastCalled: true }
          : { ...params, lastCalled: false },
      );
    } else {
      recognizedFindParams.current.push({
        ...newFindParams,
        lastCalled: true,
        hasMoreData: true,
      });
    }
  };

  const handleLoadMoreOptions = useCallback(
    (queryToSearch?: string) => {
      const currentFindParams = recognizedFindParams.current.find(
        (params) => params.queryToSearchMore === (queryToSearch || undefined),
      );

      if (currentFindParams && !currentFindParams.hasMoreData) {
        return;
      }

      const skipParam = currentFindParams ? currentFindParams.skip + currentFindParams.limit : 0;
      const newFindParams = {
        skip: skipParam,
        limit: DEFAULT_LIMIT,
        queryToSearchMore: queryToSearch || undefined,
      };

      const hasMoreData =
        currentFindParams?.lastCalled && rawDynamicData
          ? rawDynamicData.count >= currentFindParams.skip
          : true;

      updateRecognizedFindParams({ hasMoreData, newFindParams, currentFindParams });

      if (!hasMoreData) {
        return;
      }

      if (!canFetchData) {
        setCanFetchData(true);
        return;
      }

      handleSetFindParams(newFindParams);
    },
    [canFetchData, findParams, rawDynamicData],
  );
  const handleLoadSpecifOptions = useCallback(
    (selectedValues: OptionValue[]) => {
      handleSetFindParams({
        ...findParams,
        optionsToFind: selectedValues,
        limit: selectedValues.length,
      });
      setCanFetchData(true);
    },
    [findParams],
  );

  const getOptionsValuesSet = (options: Option[]) =>
    new Set(
      options
        .map((option) => [
          option.children?.length
            ? option.children.map((childOption) => childOption.value)
            : option.value,
        ])
        .flat(2),
    );

  const getAllOptionsValuesSet = () => getOptionsValuesSet(allOptionsRef.current);
  const getOnlyNewRecords = (data?: ExtractPaginationType<QueryOutput>[]) => {
    const existingValuesSet = getAllOptionsValuesSet();
    const onlyNewRecords = (data || []).filter(
      (dataWithValue) => !existingValuesSet.has(dataWithValue[valueKey]),
    );
    return onlyNewRecords;
  };
  const createOption = (record: ExtractPaginationType<QueryOutput>): Option => ({
    optionRawData: record,
    label: formatLabel(record),
    chipLabel: formatChipLabel ? formatChipLabel(record) : undefined,
    value: record[valueKey],
  });

  const getNewAllOptionsWithAggregating = (newRecords: ExtractPaginationType<QueryOutput>[]) => {
    if (!aggregatingOptionParams) {
      return [];
    }

    const newAllOptions = [...allOptions];
    newRecords.forEach((newRecord) => {
      const aggregatorKey = newRecord[aggregatingOptionParams.aggregatingValueKey];
      const isSubOption = !!aggregatorKey;
      const aggregatorValue = isSubOption ? aggregatorKey : newRecord[valueKey];

      let aggregator = newAllOptions.find((option) => option.value === aggregatorValue);

      const getChipLabel = () => {
        if (!formatChipLabel) {
          return undefined;
        }
        return isSubOption
          ? newRecord[aggregatingOptionParams.aggregatingLabelKey]
          : formatChipLabel(newRecord);
      };

      if (!aggregator) {
        aggregator = {
          value: aggregatorValue,
          label: isSubOption
            ? newRecord[aggregatingOptionParams.aggregatingLabelKey]
            : formatLabel(newRecord),
          chipLabel: getChipLabel(),
          children: [],
        };

        newAllOptions.push(aggregator);
        return;
      }

      aggregator.children!.push(createOption(newRecord));
    });

    return newAllOptions;
  };
  const handleSetNewAllOptionsWithAggregating = (data?: ExtractPaginationType<QueryOutput>[]) => {
    if (!aggregatingOptionParams) {
      return;
    }

    const onlyNewRecords = getOnlyNewRecords(data);
    if (onlyNewRecords.length <= 0) {
      return;
    }

    const newAllOptions = getNewAllOptionsWithAggregating(onlyNewRecords);
    setAllOptions(newAllOptions);
  };
  const handleSetNewAllOptionsWithoutAggregating = (
    data?: ExtractPaginationType<QueryOutput>[],
  ) => {
    if (!data?.length) {
      return;
    }

    setAllOptions((prevOptions) => {
      const existingValuesSet = getOptionsValuesSet(prevOptions);
      const formattedNewOptions = data
        .filter((record) => !existingValuesSet.has(record[valueKey]))
        .map(createOption);

      if (formattedNewOptions.length <= 0) {
        return prevOptions;
      }

      return [...prevOptions, ...formattedNewOptions];
    });
  };
  const handleSetNewOptions = (data?: ExtractPaginationType<QueryOutput>[]) => {
    if (!aggregatingOptionParams) {
      handleSetNewAllOptionsWithoutAggregating(data);
      return;
    }

    handleSetNewAllOptionsWithAggregating(data);
  };
  useEffect(() => {
    if (!rawDynamicData) {
      return;
    }
    handleSetNewOptions(rawDynamicData?.data);
  }, [rawDynamicData]);

  const normalizeSelectedValuesAfterLoadSpecifOptions = (
    data?: ExtractPaginationType<QueryOutput>[],
  ) => {
    const newRecordsFromSpecificOptionsLoad = getOnlyNewRecords(data);
    const newOptions = newRecordsFromSpecificOptionsLoad.map(createOption);

    const newAndOldOptions = [...allOptions, ...newOptions];

    normalizeSelectedValues(newAndOldOptions);
  };
  useEffect(() => {
    const hasAlreadyNormalized = hasNormalizedInitialValues.current;
    const hasInitialSelectedValues = initialSelectedValuesFromSearchParams.length > 0;

    if (hasAlreadyNormalized) {
      return;
    }

    if (hasInitialSelectedValues && isFetched && isFetchedAfterMount) {
      normalizeSelectedValuesAfterLoadSpecifOptions(rawDynamicData?.data);
      hasNormalizedInitialValues.current = true;
    }
  }, [isFetched]);

  const handleResetFilter = () => {
    recognizedFindParams.current = [];
    setFindParams(DEFAULT_FIND_PARAMS as QueryParams);
    setAllOptions([]);
    setSelectedOptions([]);
  };

  const loadDefaultValuesNotLoaded = async (existingValuesSet: Set<OptionValue>) => {
    if (!defaultValues) {
      return;
    }

    const defaultSelectedValues = formatDefaultValues(defaultValues);

    const valuesToLoad = defaultSelectedValues.filter((value) => !existingValuesSet.has(value));
    const hasValuesToLoad = valuesToLoad.length > 0;

    if (hasValuesToLoad) {
      const response = await queryFn(
        mountFindParams({ limit: valuesToLoad.length, skip: 0, optionsToFind: valuesToLoad }),
      );
      if (!response.ok || !response.data) {
        return null;
      }
      handleSetNewOptions(response.data.data as ExtractPaginationType<QueryOutput>[]);
    }

    hasLoadedDefaultValuesRef.current = true;
  };

  const handleResetToDefaultValue: HandleResetToDefaultValue = async () => {
    if (!defaultValues || isDisabled) {
      cleanFilter();
      return;
    }

    const hasLoadedDefaultValues = hasLoadedDefaultValuesRef.current;
    if (!hasLoadedDefaultValues) {
      await loadDefaultValuesNotLoaded(getAllOptionsValuesSet());
    }

    const formattedDefaultValues = formatDefaultValues(defaultValues!);

    setSelectedOptions(formattedDefaultValues);
    normalizeSelectedValues(allOptions);
  };

  return {
    key,
    rawDynamicData,
    isLoading,
    allOptions,
    selectedOptions,
    setSelectedOptions,
    handleLoadMoreOptions,
    handleLoadSpecifOptions,
    hasSelectedOptions,
    handleUpdateInternalState,
    handleResetFilter,
    cleanFilter,
    getSearchParamKeys,
    handleResetToDefaultValue,
  };
};

export default useMultiDynamicOptionsSelector;
